import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

const NAV = [
  { to: '/', label: '每日精选', icon: 'fa-fire', end: true },
  { to: '/topics', label: '主题', icon: 'fa-layer-group' },
  { to: '/weekly', label: '周报', icon: 'fa-calendar-week' },
  { to: '/starred', label: '收藏', icon: 'fa-star' },
  { to: '/agent', label: 'Agent接入', icon: 'fa-plug' },
  { to: '/about', label: '关于', icon: 'fa-info-circle' },
];

const THEMES = [
  { key: 'auto', label: '自动', icon: 'fa-adjust' },
  { key: 'light', label: '浅色', icon: 'fa-sun' },
  { key: 'dark', label: '深色', icon: 'fa-moon' },
];

const COLLAPSE_KEY = 'sidebar-collapsed';

export default function Sidebar() {
  const { mode, setMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_KEY) === '1'
  );

  const submitSearch = (e) => {
    e.preventDefault();
    const q = searchQ.trim();
    if (q) navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  // 解读页（/article/:id）按来源页高亮对应 tab；优先 state.from，兜底 query.from（中键/右键新标签页打开）
  const articleFrom = location.pathname.startsWith('/article/')
    ? (location.state?.from || new URLSearchParams(location.search).get('from') || '')
    : '';

  useEffect(() => {
    document.body.classList.toggle('sidebar-collapsed', collapsed);
    return () => document.body.classList.remove('sidebar-collapsed');
  }, [collapsed]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      return next;
    });
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const nav = (
    <aside className={`sidebar${open ? ' open' : ''}${collapsed ? ' collapsed' : ''}`}>
      <div className="sidebar-head">
        <div className="sidebar-brand">
          <span className="brand-mark">
            <i className="fas fa-book-open" />
          </span>
          {!collapsed && <span className="brand-text">木兰论文解读</span>}
        </div>
        <button
          className="collapse-btn"
          onClick={toggleCollapsed}
          title={collapsed ? '展开侧边栏' : '收缩侧边栏'}
        >
          <i className={`fas fa-angle-double-${collapsed ? 'right' : 'left'}`} />
        </button>
      </div>
      {collapsed ? (
        <button
          className="sidebar-search-icon"
          onClick={() => navigate('/search')}
          title="搜索论文解读"
        >
          <i className="fas fa-search" />
        </button>
      ) : (
        <form className="sidebar-search" onSubmit={submitSearch}>
          <i className="fas fa-search" />
          <input
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="搜索论文解读..."
          />
          {searchQ && (
            <button type="button" className="search-clear" onClick={() => setSearchQ('')} title="清空搜索词">
              <i className="fas fa-times" />
            </button>
          )}
        </form>
      )}
      <nav className="side-nav">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `side-link${isActive || (articleFrom && articleFrom === item.to) ? ' active' : ''}`}
            title={item.label}
          >
            <i className={`fas ${item.icon}`} />
            {!collapsed && <span className="side-label">{item.label}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="sidebar-footer">
        <div className="theme-switch">
          {THEMES.map((t) => (
            <button
              key={t.key}
              className={`theme-btn${mode === t.key ? ' active' : ''}`}
              onClick={() => setMode(t.key)}
              title={t.label}
            >
              <i className={`fas ${t.icon}`} />
              {!collapsed && <span className="side-label">{t.label}</span>}
            </button>
          ))}
        </div>
      </div>
    </aside>
  );

  return (
    <>
      <button className="menu-toggle" onClick={() => setOpen((v) => !v)} aria-label="菜单">
        <i className={`fas ${open ? 'fa-times' : 'fa-bars'}`} />
      </button>
      {nav}
      {open && <div className="sidebar-backdrop" onClick={() => setOpen(false)} style={{ display: 'block' }} />}
    </>
  );
}
